import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Grid,
  Fade,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Person as PersonIcon,
  LoginOutlined as LoginIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccessful, setLoginSuccessful] = useState(false);
  const [inactivityLogout, setInactivityLogout] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Verificar se o usuário foi desconectado por inatividade
  useEffect(() => {
    const logoutReason = localStorage.getItem('logoutReason');
    if (logoutReason === 'inatividade') {
      setInactivityLogout(true);
      // Limpar a razão do logout para não mostrar a mensagem novamente
      localStorage.removeItem('logoutReason');
    }
  }, []);

  const handleSubmit = async (event) => {
    console.log("🚀 LOGIN: handleSubmit chamado!");
    event.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setInactivityLogout(false); // Esconder a mensagem de inatividade ao tentar fazer login
      
      // Usar URL da API com /api para funcionar com o backend Flask
      const { getApiUrl } = await import('../config/api');
      const apiUrl = getApiUrl();
      console.log("🔧 DEBUG: apiUrl final =", apiUrl);
      console.log("📤 LOGIN: Dados sendo enviados:", {
        username,
        password: password ? "***PREENCHIDA***" : "VAZIA",
        endpoint: `${apiUrl}/login`
      });

      const response = await axios.post(`${apiUrl}/login`, {
        username,
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log("📥 LOGIN: Resposta da API:", response);
      console.log("📥 LOGIN: response.data:", response.data);
      console.log("📥 LOGIN: Status:", response.status);
      
      if (response.data && response.data.token) {
      const { token, user } = response.data;
        console.log("✅ LOGIN: Token recebido:", token ? token.substring(0, 20) + '...' : 'NULO');
        console.log("✅ LOGIN: Usuário recebido:", user);
      
      // Salvar token e informações do usuário
        console.log('🔐 LOGIN: Salvando token no localStorage...', {
          token: token.substring(0, 20) + '...',
          user: user.nome,
          timestamp: Date.now()
        });
        
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('tokenCreatedAt', Date.now().toString());
        
        console.log('🔐 LOGIN: Token salvo! localStorage atual:', {
          token: localStorage.getItem('token') ? 'EXISTE' : 'NÃO EXISTE',
          user: localStorage.getItem('user') ? 'EXISTE' : 'NÃO EXISTE',
          tokenCreatedAt: localStorage.getItem('tokenCreatedAt'),
          allKeys: Object.keys(localStorage)
        });
      
      // Mostrar mensagem de sucesso
      setLoginSuccessful(true);
      
      // Redirecionar com base no primeiro acesso
      console.log('🚀 LOGIN: Iniciando redirecionamento...', {
        primeiro_acesso: user.primeiro_acesso,
        destino: user.primeiro_acesso ? '/alterar-senha' : '/dashboard'
      });
      
      setTimeout(() => {
      if (user.primeiro_acesso) {
        window.location.href = '/alterar-senha';
        } else {
          window.location.href = '/importacao'; // Redirecionar direto para importacao
        }
              }, 100); // Pequeno delay para garantir que o localStorage foi salvo
      } else {
        console.error("❌ LOGIN: Resposta inválida - token não encontrado!");
        console.error("❌ LOGIN: response.data:", response.data);
        setError('Resposta inválida do servidor. Tente novamente.');
      }
      
    } catch (err) {
      console.error("❌ LOGIN: Erro ao fazer login:", err);
      console.error("❌ LOGIN: err.response:", err.response);
      console.error("❌ LOGIN: err.response?.data:", err.response?.data);
      console.error("❌ LOGIN: err.response?.status:", err.response?.status);
      setError(err.response?.data?.message || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const AppLogo = () => (
    <Typography 
      variant="h4" 
      component="div" 
      align="center"
      fontWeight="bold"
      color="primary"
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: 1,
        mb: 3
      }}
    >
      <LockIcon fontSize="large" />
      Sistema Administrativo
    </Typography>
  );

  return (
    <Fade in={true} timeout={800}>
      <Container maxWidth="md" sx={{ height: '100vh', display: 'flex', alignItems: 'center' }}>
        <Grid container spacing={0} sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
          {/* Lado esquerdo - Imagem/Banner (apenas em telas maiores) */}
          {!isMobile && (
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  height: '100%',
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: 'white',
                  backgroundImage: 'linear-gradient(45deg, #2196f3, #3f51b5)',
                }}
              >
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Bem-vindo ao Sistema
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Faça login para acessar todas as funcionalidades de administração e gerenciamento.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Recursos disponíveis:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body2">Gestão de usuários</Typography>
                    <Typography component="li" variant="body2">Gestão de empresas</Typography>
                    <Typography component="li" variant="body2">Importação de dados</Typography>
                    <Typography component="li" variant="body2">Relatórios e análises</Typography>
                  </Box>
                </Box>
                <Typography variant="caption">
                  © {new Date().getFullYear()} - Todos os direitos reservados
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Lado direito - Formulário de login */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 3, md: 4 }, 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              {isMobile && <AppLogo />}
              
              <Typography variant="h5" component="h1" gutterBottom align="center">
                {isMobile ? 'Login' : 'Acesso ao Sistema'}
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 4 }}>
                Digite suas credenciais para acessar o sistema
              </Typography>

              {inactivityLogout && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 3 }}
                  icon={<AccessTimeIcon />}
                >
                  <Typography variant="subtitle2">Sessão expirada por inatividade</Typography>
                  <Typography variant="body2">
                    Sua sessão foi encerrada automaticamente após 20 minutos sem atividade. Por favor, faça login novamente.
                  </Typography>
                </Alert>
              )}

              {error && !inactivityLogout && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {loginSuccessful && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Login realizado com sucesso! Redirecionando...
                </Alert>
              )}

              <form onSubmit={handleSubmit} action="" method="post">
                <TextField
                  label="Nome de usuário"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  label="Senha"
                  variant="outlined"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  disabled={loading || loginSuccessful}
                  startIcon={loading ? undefined : <LoginIcon />}
                  sx={{ py: 1.5, mt: 3 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Entrar'}
                </Button>
              </form>

              <Stack spacing={2} direction="column" sx={{ mt: 4 }}>
                <Typography variant="body2" color="textSecondary" align="center">
                  Não tem uma conta? Entre em contato com o administrador do sistema.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Fade>
  );
};

export default Login;
